import { createSlice } from '@reduxjs/toolkit';
import { SustainabilityQuestionDto } from '../../models/sustainability';
import sustainabilityApi from '../apis/sustainability.api';

/** profileType -> section -> questions */
type QuestionsByProfileTypeAndSection = Record<number, Record<string, SustainabilityQuestionDto[]>>;
type QuestionsById = Record<number, SustainabilityQuestionDto>;

const initialState: {
	data: QuestionsByProfileTypeAndSection;
	byId: QuestionsById;
} = {
	data: {},
	byId: {}
};

const sustainabilityQuestionsSlice = createSlice({
	name: 'sustainabilityQuestions',
	initialState,
	reducers: {
		clearSustainabilityQuestions(state) {
			state.data = {};
			state.byId = {};
		}
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			sustainabilityApi.endpoints.getQuestionsBySection.matchFulfilled,
			(state, action) => {
				const { profileType, section } = action.meta.arg.originalArgs;
				const incoming = action.payload ?? [];

				if (state.data[profileType] === undefined) {
					state.data[profileType] = {};
				}

				state.data[profileType][section] = [...incoming].sort(
					(a, b) => a.sequenceOrder - b.sequenceOrder
				);

				for (const question of incoming) {
					state.byId[question.id] = question;
				}
			}
		);

		builder.addMatcher(
			sustainabilityApi.endpoints.getQuestionById.matchFulfilled,
			(state, action) => {
				const question = action.payload;
				if (!question) return;

				state.byId[question.id] = question;
			}
		);
	}
});

export const { clearSustainabilityQuestions } = sustainabilityQuestionsSlice.actions;

type SustainabilityQuestionsState = {
	sustainabilityQuestions: { data: QuestionsByProfileTypeAndSection; byId: QuestionsById };
};

export const selectSustainabilityQuestions = (
	state: SustainabilityQuestionsState,
	profileType: number,
	section: string
): SustainabilityQuestionDto[] =>
	state.sustainabilityQuestions?.data?.[profileType]?.[section] ?? [];

export const selectSustainabilityQuestionById = (
	state: SustainabilityQuestionsState,
	questionId: number
): SustainabilityQuestionDto | null => state.sustainabilityQuestions?.byId?.[questionId] ?? null;

export default sustainabilityQuestionsSlice.reducer;
